import styled from '@emotion/styled/macro';
import {
  APPOINTMENT_STATUS_COMPLETED,
  APPOINTMENT_STATUS_CANCELLED,
  APPOINTMENT_STATUS_SET,
  APPOINTMENT_STATUS_SKEDULO_CANCELLED,
  APPOINTMENT_STATUS_IN_PROGRESS,
  APPOINTMENT_STATUS_NOT_DISPOSITIONED,
  APPOINTMENT_STATUS_PENDING_ALLOCATION,
  APPOINTMENT_STATUS_CHECK_IN_SET,
  APPOINTMENT_STATUS_CHECK_IN_CANCELLED,
  APPOINTMENT_STATUS_CHECK_IN_COMPLETED,
  Pill,
  SalesAppointment,
  SalesAppointmentStatus,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from '@sunrun/experience-ui-components';
import { formatAppointmentDateTime } from 'helpers/formatHelpers';

const statusMap: Record<SalesAppointmentStatus, StatusMapType> = {
  [APPOINTMENT_STATUS_SET]: {
    statusText: 'Scheduled',
    statusType: 'info',
  },
  [APPOINTMENT_STATUS_COMPLETED]: {
    statusText: 'Completed',
    statusType: 'success',
  },
  [APPOINTMENT_STATUS_CANCELLED]: {
    statusText: 'Cancelled',
    statusType: 'warning',
  },
  [APPOINTMENT_STATUS_SKEDULO_CANCELLED]: {
    statusText: 'Cancelled',
    statusType: 'warning',
  },
  [APPOINTMENT_STATUS_IN_PROGRESS]: {
    statusText: 'Scheduled',
    statusType: 'info',
  },
  [APPOINTMENT_STATUS_NOT_DISPOSITIONED]: {
    statusText: 'Scheduled',
    statusType: 'warning',
  },
  [APPOINTMENT_STATUS_PENDING_ALLOCATION]: {
    statusText: 'Scheduled',
    statusType: 'warning',
  },
  [APPOINTMENT_STATUS_CHECK_IN_SET]: {
    statusText: 'Scheduled',
    statusType: 'info',
  },
  [APPOINTMENT_STATUS_CHECK_IN_CANCELLED]: {
    statusText: 'Cancelled',
    statusType: 'warning',
  },
  [APPOINTMENT_STATUS_CHECK_IN_COMPLETED]: {
    statusText: 'Completed',
    statusType: 'success',
  },
};

type AppointmentViewProps = {
  appointment: SalesAppointment;
  timezone: string;
};

const isDispositionDue = (status: string, startTime: string) => {
  const now = new Date().getTime();
  const startTimeTime = new Date(startTime).getTime();
  const isPastAppointment = startTimeTime < now;

  if (status === 'Appointment Set' && isPastAppointment) {
    return true;
  }
  return false;
};

type StatusType = 'info' | 'success' | 'warning' | 'error';

type StatusMapType = {
  statusText: string;
  statusType: StatusType;
};
const appointmentStatusMap = (
  status: SalesAppointmentStatus,
  startTime: string,
): StatusMapType => {
  if (isDispositionDue(status, startTime)) {
    return {
      statusText: 'Disposition due',
      statusType: 'error',
    };
  }

  const result = statusMap[status];
  return result ?? { statusText: 'Unknown', statusType: 'error' };
};

const AppointmentView = ({ appointment, timezone }: AppointmentViewProps) => {
  const { dateTime, status, type } = appointment;

  const { statusText, statusType } = appointmentStatusMap(
    status as SalesAppointmentStatus,
    dateTime,
  );

  return (
    <UpcomingAppointment>
      <HomeReviewLayout>
        <div>
          <TitleRow>
            <SvgIcon
              style={{ display: 'flex', marginRight: 4 }}
              width={16}
              height={16}
              color={tokens.TINTS_OFF_WHITE_20}
              name={SvgNames.Appointments}
            />{' '}
            <Typography size={12}>{type}</Typography>
          </TitleRow>

          <Typography size={12} style={{ display: 'block', marginLeft: 20 }}>
            {formatAppointmentDateTime(appointment, timezone)}
          </Typography>
        </div>
        <SurveyPill text={statusText} type={statusType} hideIcon />
      </HomeReviewLayout>
    </UpcomingAppointment>
  );
};

const TitleRow = styled.div`
  display: flex;
  align-items: end;
`;

const HomeReviewLayout = styled.div`
  display: flex;
  justify-content: space-between;
`;

const SurveyPill = styled(Pill)`
  padding: 4px 6px !important;
  min-height: auto !important;
  align-self: center;

  > p {
    line-height: normal;
  }
`;

const UpcomingAppointment = styled.div`
  color: ${tokens.TINTS_OFF_WHITE_20};
  font-size: 12px;
`;

export { AppointmentView };
